import React, { useEffect } from "react";
import TalentCard from "../components/TalentCard";
import AOS from "aos";
import "aos/dist/aos.css";

// ✅ clean Unsplash images (no trailing spaces)
const artist1 = "https://images.unsplash.com/photo-1487215078519-e21cc028cb29";
const artist2 = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f";
const artist3 = "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4";

// ✅ All artists updated with realistic, professional roles
const artists = [
  { 
    name: "Eldridge James", 
    role: "Poet, Musician, Visionary", 
    image: artist1, 
    socials: ["@_eldridgejames"] 
  },
  { 
    name: "Stickman Tenny", 
    role: "Hip Hop Artist & Creative", 
    image: artist2, 
    socials: ["@stickman_tenny"] 
  },
  { 
    name: "Major8ohwait", 
    role: "Hip Hop Artist & Songwriter", 
    image: artist3, 
    socials: ["@major8ohwait"] 
  },
];

const Artists = ({ darkTheme = true }) => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const getStyles = (dark) => ({
    pageContainer: {
      paddingTop: "80px",
    },
    heroSection: {
      padding: "6rem 2rem 4rem",
      backgroundColor: dark ? "#0a0a0a" : "#f5f5f5",
      textAlign: "center",
    },
    heroTitle: {
      fontSize: "3rem",
      fontWeight: "700",
      marginBottom: "1.5rem",
      color: dark ? "#fff" : "#111",
      lineHeight: "1.2",
    },
    heroSubtitle: {
      fontSize: "1.2rem",
      color: dark ? "#aaa" : "#666",
      lineHeight: "1.8",
      maxWidth: "800px",
      margin: "0 auto",
    },
    mainSection: {
      padding: "5rem 2rem",
      backgroundColor: dark ? "#111" : "#fff",
    },
    sectionTitle: {
      fontSize: "2rem",
      fontWeight: "700",
      marginBottom: "1.5rem",
      color: dark ? "#fff" : "#111",
      textAlign: "center",
    },
    sectionText: {
      fontSize: "1.1rem",
      color: dark ? "#ccc" : "#555",
      lineHeight: "1.8",
      maxWidth: "900px",
      margin: "0 auto 4rem",
      textAlign: "center",
    },
    talentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "2rem",
      maxWidth: "1400px",
      margin: "0 auto",
      justifyItems: "center",
    },
    servicesSection: {
      padding: "5rem 2rem",
      backgroundColor: dark ? "#0a0a0a" : "#f5f5f5",
    },
    servicesList: {
      maxWidth: "900px",
      margin: "3rem auto 0",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "2rem",
    },
    serviceCard: {
      padding: "2rem",
      backgroundColor: dark ? "#111" : "#fff",
      borderRadius: "12px",
      border: `1px solid ${dark ? "#222" : "#e5e5e5"}`,
      transition: "all 0.3s ease",
    },
    serviceIcon: {
      fontSize: "2.5rem",
      marginBottom: "1rem",
      display: "block",
    },
    serviceTitle: {
      fontSize: "1.3rem",
      fontWeight: "600",
      marginBottom: "0.8rem",
      color: dark ? "#fff" : "#111",
    },
    serviceDescription: {
      fontSize: "1rem",
      color: dark ? "#aaa" : "#666",
      lineHeight: "1.7",
    },
  });

  const styles = getStyles(darkTheme);

  return (
    <div style={styles.pageContainer}>
      {/* HERO SECTION */}
      <section style={styles.heroSection}>
        <div data-aos="fade-down">
          <span style={{
            color: "#ff0000",
            fontSize: "0.9rem",
            fontWeight: "600",
            letterSpacing: "2px",
            textTransform: "uppercase",
            display: "block",
            marginBottom: "1rem",
          }}>
            OUR ROSTER
          </span>
          <h1 style={styles.heroTitle}>Artists Management</h1>
          <p style={styles.heroSubtitle}>
            We represent talented artists in AmaPiano, Hip Hop, and RnB. Our focus on 
            world-renowned genres speaks to our niche market as a professional agency.
          </p>
        </div>
      </section>

      {/* ARTISTS GRID */}
      <section style={styles.mainSection}>
        <h2 style={styles.sectionTitle}>Featured Artists</h2>
        <p style={styles.sectionText}>
          Meet our talented roster of artists who are making waves in the music industry.
          We provide comprehensive support to help them reach their full potential.
        </p>
        <div style={styles.talentGrid} data-aos="fade-up">
          {artists.map((artist, i) => (
            <TalentCard key={i} {...artist} darkTheme={darkTheme} />
          ))}
        </div>
      </section>

      {/* SERVICES FOR ARTISTS */}
      <section style={styles.servicesSection}>
        <h2 style={styles.sectionTitle}>What We Offer Artists</h2>
        <p style={styles.sectionText}>
          Comprehensive management services tailored to develop and grow your music career
        </p>
        <div style={styles.servicesList} data-aos="fade-up">
          {[
            {
              icon: "🎤",
              title: "Branding & Image",
              description: "Develop a unique brand identity that resonates with your audience",
            },
            {
              icon: "🎵",
              title: "Music Production",
              description: "Connect with top producers and studios for quality recordings",
            },
            {
              icon: "🎪",
              title: "Tour Management",
              description: "Organize and coordinate national and international tours",
            },
            {
              icon: "📱",
              title: "Social Media",
              description: "Strategic digital marketing and social media growth",
            },
            {
              icon: "🤝",
              title: "Bookings",
              description: "Secure performance opportunities and negotiate contracts",
            },
            {
              icon: "💼",
              title: "Career Strategy",
              description: "Long-term planning and strategic career development",
            },
          ].map((service, i) => (
            <div key={i} style={styles.serviceCard} data-aos="zoom-in" data-aos-delay={i * 100}>
              <span style={styles.serviceIcon}>{service.icon}</span>
              <h3 style={styles.serviceTitle}>{service.title}</h3>
              <p style={styles.serviceDescription}>{service.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Artists;